// src/lib/nexilink-card-styles.ts
// ✨ Reusable Nexilink card styles to ensure consistency

/**
 * Standard Nexilink card style (neutral-50 background)
 * - Neutral-50 background
 * - 2px gray-100 border
 * - No shadows
 * - Rounded-2xl corners
 */
export const NEXILINK_CARD = 'bg-neutral-50 rounded-2xl border-2 border-gray-100';

/**
 * Dashboard card style (white background)
 * - White background for use on gray/neutral page backgrounds
 * - 2px gray-100 border
 * - No shadows
 * - Rounded-2xl corners
 */
export const NEXILINK_CARD_WHITE = 'bg-white rounded-2xl border-2 border-gray-100';

/**
 * Clickable/hoverable card variant
 * Adds hover effects for interactive cards
 */
export const NEXILINK_CARD_HOVER = `${NEXILINK_CARD} transition-all duration-200 hover:border-gray-200 cursor-pointer`;

/**
 * Clickable/hoverable white card variant
 * Adds hover effects for interactive cards
 */
export const NEXILINK_CARD_WHITE_HOVER = `${NEXILINK_CARD_WHITE} transition-all duration-200 hover:border-gray-200 cursor-pointer`;

/**
 * Card with padding (standard 6 units = 24px)
 */
export const NEXILINK_CARD_PADDED = `${NEXILINK_CARD} p-6`;

/**
 * White card with padding (standard 6 units = 24px)
 */
export const NEXILINK_CARD_WHITE_PADDED = `${NEXILINK_CARD_WHITE} p-6`;

/**
 * Elevated white container card — subtle depth (soft shadow + hover lift).
 * Use for TOP-LEVEL container cards/panels to avoid the flat "boring" look.
 * (Keep NEXILINK_CARD_WHITE flat for list-row items so lists don't feel cluttered.)
 */
export const NEXILINK_CARD_WHITE_ELEVATED =
    'bg-white rounded-2xl border border-gray-100 shadow-sm transition-all duration-200 hover:shadow-md';

/**
 * Standard list-row surface — flat, with a smooth hover highlight. The single
 * row treatment across all lists (replaces ad-hoc bordered card-per-row blocks).
 */
export const NEXILINK_ROW =
    'flex items-center gap-3 px-4 py-3.5 rounded-xl transition-colors hover:bg-gray-50';

/**
 * Card section divider (matches border style)
 */
export const NEXILINK_DIVIDER = 'border-t-2 border-gray-100';

/**
 * Vertical divider
 */
export const NEXILINK_DIVIDER_VERTICAL = 'border-l-2 border-gray-100';

/**
 * Standard spacing between cards and sections
 */
export const NEXILINK_SPACING = 'space-y-6';

/**
 * Grid gap for card layouts
 */
export const NEXILINK_GAP = 'gap-6';

/**
 * Export all as a single object for convenience
 */
export const NexilinkStyles = {
    card: NEXILINK_CARD,
    cardWhite: NEXILINK_CARD_WHITE,
    cardHover: NEXILINK_CARD_HOVER,
    cardWhiteHover: NEXILINK_CARD_WHITE_HOVER,
    cardPadded: NEXILINK_CARD_PADDED,
    cardWhitePadded: NEXILINK_CARD_WHITE_PADDED,
    divider: NEXILINK_DIVIDER,
    dividerVertical: NEXILINK_DIVIDER_VERTICAL,
    spacing: NEXILINK_SPACING,
    gap: NEXILINK_GAP,
} as const;

/**
 * Helper function to combine Nexilink card style with custom classes
 */
export function nexilinkCard(additionalClasses?: string): string {
    return additionalClasses ? `${NEXILINK_CARD} ${additionalClasses}` : NEXILINK_CARD;
}

/**
 * Helper function to combine white card style with custom classes
 */
export function nexilinkCardWhite(additionalClasses?: string): string {
    return additionalClasses ? `${NEXILINK_CARD_WHITE} ${additionalClasses}` : NEXILINK_CARD_WHITE;
}

/**
 * Helper function for padded cards with custom classes
 */
export function nexilinkCardPadded(additionalClasses?: string): string {
    return additionalClasses ? `${NEXILINK_CARD_PADDED} ${additionalClasses}` : NEXILINK_CARD_PADDED;
}

/**
 * Helper function for padded white cards with custom classes
 */
export function nexilinkCardWhitePadded(additionalClasses?: string): string {
    return additionalClasses ? `${NEXILINK_CARD_WHITE_PADDED} ${additionalClasses}` : NEXILINK_CARD_WHITE_PADDED;
}